import { takeUntil } from 'rxjs/operators';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { IPlate } from './../parking-helper';
import { AfterViewChecked, ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { AuthService } from '../../../base/services/auth.service';
import {
  CheckinUserParkingDTO,
  CheckinUserParkingRegistrationDTO,
  CheckoutUserParkingDTO,
  ControlPlateType,
  MultipleCarsPerUserTreatAs,
  ParkerRegistrationOffense,
  ParkingProcessTariffInfoDTO,
  ParkingProcessZoneInfoDTO,
  ParkingQuotaDuration,
  ParkingQuotaInfoDTO,
  UserInfoDTO,
} from '../../../base/models/models.swagger.generated';
import { DropDownConstantsFactory } from '../../../base/shared/syncfusion/dropdown-constants-factory';
import { FormGroup, Validators, ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { CountryISO2 } from '../../../base/shared/countryISO2-enum';
import { EnumTranslationService } from '../../../base/services/enum-translation.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ParkingService } from './parking.service';
import { ParkingHelper } from '../parking-helper';
import { StringHelper } from '../../../base/shared/string-helper';
import { DateTime } from 'luxon';
import { interval, Subject, Subscription } from 'rxjs';
import { EnumDropDownListComponent } from '../../../base/shared/enum-drop-down-list/enum-drop-down-list.component';
import { AppErrorMessagesComponent } from '../../../base/error-messages/app-error-messages.component';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { NgClass } from '@angular/common';
import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { ParkingFormType } from './parking-form-type';
import { ParkingQuotaInfoTextParams } from '../helpers/parking-quota-info-text-params';
import { MaxParkingDurationHelper } from '../helpers/max-parking-duration.helper';
import { ParkingTimesHelper } from '../helpers/parking-times.helper';

@Component({
    selector: 'app-parking',
    templateUrl: './parking.component.html',
    styleUrls: ['./parking.component.scss'],
    imports: [
        TitleBarComponent,
        ReactiveFormsModule,
        DropDownListModule,
        AppErrorMessagesComponent,
        NgClass,
        EnumDropDownListComponent,
        TranslateModule
    ]
})
export class ParkingComponent implements OnInit, AfterViewChecked, OnDestroy {

  public isCheckedIn = false;
  public dataLoaded = false;

  public enumDropdownSettings = DropDownConstantsFactory.createEnumDropdownSettings();
  public clientDropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public ControlPlateType = ControlPlateType;
  public countryList = [];

  public isParkingPossible = false;
  public hasTariffs = false;
  public maxEndTime: DateTime;

  public errors: { [key: string]: string } = {};

  public parkingEndTimeText = '';

  public selectedParkingProcessInfoIndex = 0;

  public parkingQuotaInfoTextParams: ParkingQuotaInfoTextParams;

  public parkingForm: FormGroup<ParkingFormType> = this.fb.group<ParkingFormType>({
    clientId: this.fb.control<number>(null, Validators.required),
    zoneId: this.fb.control<number>(null, Validators.required),
    subscriptionAssignmentId: this.fb.control<number>(null, Validators.required),
    parkingSpaceNumber: this.fb.control<string>(null),
    controlPlateId: this.fb.control<number>(null, Validators.required),
    newVehicle: this.fb.group({
      type: this.fb.control<ControlPlateType>(null, Validators.required),
      countryCode: this.fb.control<string>(null, Validators.required),
      numberPlate: this.fb.control<string>(null, [Validators.required, Validators.maxLength(10)]),
    }),
    /* parkingDuration: this.fb.group({
      durationForm: this.fb.group({
        days: [null, Validators.min(0)],
        hours: [null, Validators.min(0)],
        minutes: [null, Validators.min(0)]
      }, {validators: [CustomValidatorsFactory.atLeastOneFromFormGroupRequired()]}),
      maxParkingDuration: [null]
    }, {validators: [ParkingComponent.durationSmallerThanMaxParkingDuration()]}) */
  });

  public get numActiveParkingSessions(): number {
    return this.parkingService.parkingProcessInfos?.length ?? 0;
  }

  public get hasFoundZones(): boolean {
    return this.parkingHelper.hasFoundZones;
  }

  public get hasFoundSubscriptions(): boolean {
    return this.parkingHelper.hasFoundSubscriptions;
  }

  public get hasFoundControlPlates(): boolean {
    return this.parkingHelper.hasFoundControlPlates;
  }

  public get clientDropdownValues(): { name: string; id: number }[] {
    return this.parkingHelper.clientDropdownValues;
  }

  public get subscriptionsDropdownValues(): { name: string; id: number }[] {
    return this.parkingHelper.subscriptionsDropdownValues;
  }

  public get zoneDropdownValues(): { name: string[]; id: number }[] {
    return this.parkingHelper.zoneDropdownValues;
  }

  public get parkingSpaceNumberDefinition(): string {
    return this.parkingHelper.parkingSpaceNumberDefinition;
  }

  public get controlPlateDropdownValues(): { name: string; id: number; plate: IPlate }[] {
    return this.parkingHelper.controlPlateDropdownValues;
  }

  public get parkingProcessZoneInfoDTO(): ParkingProcessZoneInfoDTO[] {
    return this.parkingHelper.parkingProcessZoneInfoDTO;
  }

  public get maxDuration(): string {
    if (this.maxParkingDuration) {
      return StringHelper.transformToTimeRepresentation(this.maxParkingDuration) + 'h';
    }
    return 'i18n.domain.parking.unlimited';
  }

  public get isOverMaxParkingDuration(): boolean {
    return !!this.maxEndTime ? this.maxEndTime < DateTime.now() : false;
  }

  public get isCloseToMaxParkingDuration(): boolean {
    return !!this.maxEndTime ? this.maxEndTime.minus({minutes: 10}) < DateTime.now() : false;
  }

  public get clientSelected(): boolean {
    return !!this.parkingForm.controls.clientId.value;
  }

  public get zoneSelected(): boolean {
    return !!this.parkingForm.controls.zoneId.value;
  }

  public get subscriptionSelected(): boolean {
    return !!this.parkingForm.controls.subscriptionAssignmentId.value;
  }

  public get isAddVehicle(): boolean {
    return this.parkingForm.controls.controlPlateId.value === -1;
  }

  public get hasLinkedParker(): boolean {
    return this.user.linkedParkers?.length > 0;
  }

  public get showClientDropDown(): boolean {
    return !this.checkinUserParkingRegistration || this.clientDropdownValues.find(c => c.id === this.parkingForm.controls.clientId.value) != null;
  }

  public get isParkingTimeExpired(): boolean {
    if (!this.checkinUserParkingRegistration) {
      return false;
    }
    if (this.maxParkingDuration == null) {
      return false;
    }
    const endTime = DateTime.now();
    const durationInMinutes = endTime.diff(this.checkinUserParkingRegistration.registrationDateTime, 'minutes').minutes;
    return this.maxParkingDuration < durationInMinutes;
  }

  public get isAdd(): boolean {
    return !this.checkinUserParkingRegistration;
  }

  public get vehicleGroup(): FormGroup {
    return this.parkingForm.controls.newVehicle as FormGroup;
  }

  private checkinUserParkingRegistration: CheckinUserParkingRegistrationDTO;

  private user: UserInfoDTO;
  private parkingHelper: ParkingHelper;
  private isParkingUnlimited = false;
  private readonly timerUnsubscribe: Subject<void> = new Subject();

  private maxParkingDuration: number;
  private formSubscriptions: Subscription = new Subscription();

  private parkingQuotaInfo: ParkingQuotaInfoDTO;

  constructor(private authService: AuthService,
              private fb: FormBuilder,
              private cdr: ChangeDetectorRef,
              private translate: TranslateService,
              private parkingService: ParkingService,
              private enumTranslationService: EnumTranslationService,
              spinnerService: SpinnerService) {
    this.parkingHelper = new ParkingHelper(false, this.parkingService, this.translate, spinnerService);
  }

  /* public static durationSmallerThanMaxParkingDuration(): ValidatorFn {
    return (group: FormGroup) => {
      const durationFormValue = group.controls.durationForm.value;
      const durationInMinutes = MultiFieldDurationPickerHelper.calculateTotalMinutes(durationFormValue.days, durationFormValue.hours, durationFormValue.minutes, false);
      const maxParkingDuartionInMintues = group.controls.maxParkingDuration.value;
      if (durationInMinutes && maxParkingDuartionInMintues && durationInMinutes > maxParkingDuartionInMintues) {
        return {maxDurationExceeded: true};
      }
      return null;
    };
  } */

  public getTimeElapsed(): string {
    return StringHelper.getTimeElapsed(this.checkinUserParkingRegistration.registrationDateTime);
  }

  public getMaxParkingDuration(): string {
    if (this.checkinUserParkingRegistration?.automaticScheduleEndTime != null) {
      const duration = this.checkinUserParkingRegistration?.automaticScheduleEndTime.diff(this.checkinUserParkingRegistration.registrationDateTime, 'minutes');
      return StringHelper.transformToTimeRepresentation(duration.minutes);
    }
    if (this.checkinUserParkingRegistration?.maxIntendedParkingDurationMinutes != null) {
      return StringHelper.transformToTimeRepresentation(this.checkinUserParkingRegistration?.maxIntendedParkingDurationMinutes);
    }
    return this.maxParkingDuration ? StringHelper.transformToTimeRepresentation(this.maxParkingDuration) : null;
  }

  public getMaxEndTime(): string {
    if (this.checkinUserParkingRegistration?.automaticScheduleEndTime != null) {
      return this.checkinUserParkingRegistration?.automaticScheduleEndTime.toFormat('dd.MM.yyyy HH:mm');
    }
    if (this.checkinUserParkingRegistration?.maxIntendedParkingDurationMinutes != null) {
      return this.checkinUserParkingRegistration.registrationDateTime.plus({minutes: this.checkinUserParkingRegistration?.maxIntendedParkingDurationMinutes})
        .toFormat('dd.MM.yyyy HH:mm');
    }
    return this.maxEndTime ? this.maxEndTime.toFormat('dd.MM.yyyy HH:mm') : 'i18n.domain.parking.unlimited';
  }

  public getMaxParkingEndTimeWithDuration(): string {
    if (!this.maxEndTime) {
      return 'i18n.domain.parking.unlimited';
    }
    const now = DateTime.now();
    let pre = '';
    if (this.maxEndTime && now.startOf('day').toMillis() !== this.maxEndTime.startOf('day').toMillis()) {
      pre = `(+${this.maxEndTime.startOf('day').diff(now.startOf('day'), 'days').days}) `;
    }
    return pre + this.maxEndTime?.toFormat('HH:mm') + ' (' + this.maxDuration + ')';
  }

  public getStartTime(): string {
    return this.checkinUserParkingRegistration.registrationDateTime.toFormat('dd.MM.yyyy HH:mm');
  }

  public ngOnInit(): void {
    this.user = this.authService.getUserInfo();
    this.countryList = this.enumTranslationService.translateEnum(CountryISO2, 'i18n.shared.CountryISO2.');

    this.initForm(this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex)?.activeCheckinInfo);
    const assignment = this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex)?.activeCheckinInfo?.subscriptionAssignment;
    const subscription = assignment?.subscription;
    const fallbackParkingTariffs = subscription?.denyForeignParkingSubscriptionFallback ||
                                    !this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex).activeCheckinInfo?.zone?.foreignParkersAllowed ? null :
                        this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex).activeCheckinInfo?.zone?.foreignParkingSubscription?.tariffs;
    this.updateParkingTimes(subscription?.tariffs, assignment?.validTo?.toUTC(), subscription?.maxParkingDurationInMin, fallbackParkingTariffs);
    interval(30000).pipe(
        takeUntil(this.timerUnsubscribe)
      ).subscribe(() => {
        // getTimeElapsed will be triggered automatically (duration info while is checked in) since interval is triggering refesh view
        if (!this.isCheckedIn) {
          this.refreshParkingTimesFromForm();
        }
      });
  }

  public ngAfterViewChecked(): void {
    // https://www.syncfusion.com/forums/163692/expression-changed-after-it-was-checked-when-setting-initial-value-for-control
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.formSubscriptions?.unsubscribe();
    this.timerUnsubscribe.next();
    this.timerUnsubscribe.complete();
  }

  public checkin(): void {
    const formValues = this.parkingForm.getRawValue();

    const checkinDTO = new CheckinUserParkingDTO({
      selectedSubscriptionAssignmentId: formValues.subscriptionAssignmentId,
      zoneId: formValues.zoneId,
      controlPlateType: 0
    });

    if (formValues.controlPlateId === -1) {
      checkinDTO.controlPlateType = formValues.newVehicle.type;
      checkinDTO.controlPlateCountryCode = formValues.newVehicle.countryCode;
      checkinDTO.controlPlateNumber = formValues.newVehicle.numberPlate;
    } else {
      const plateEntry = this.controlPlateDropdownValues.find((p) => p.id === formValues.controlPlateId);
      if (plateEntry) {
        checkinDTO.controlPlateType = plateEntry.plate.type;
        checkinDTO.controlPlateCountryCode = plateEntry.plate.countryCode;
        checkinDTO.controlPlateNumber = plateEntry.plate.numberPlate;
      }
    }

    if (formValues.parkingSpaceNumber) {
      checkinDTO.parkingSpaceNumber = formValues.parkingSpaceNumber;
    }

    if (this.maxEndTime) {
      checkinDTO.maxIntendedParkingDurationMinutes = Math.floor(this.maxEndTime.diff(DateTime.now().set({second: 0, millisecond: 0}), 'minutes').minutes);
    }

    this.parkingService.checkin(checkinDTO).subscribe(val => {
      this.checkinUserParkingRegistration = null;
      if (this.parkingService.parkingProcessInfos?.length) {
        this.checkinUserParkingRegistration = this.parkingService.parkingProcessInfos[this.parkingService.parkingProcessInfos.length - 1].activeCheckinInfo;
      } else {
        // checkin failed with offense
        return;
      }
      this.isCheckedIn = true;
      this.setFormFieldsEnabledState(false);
      if (this.isAddVehicle) {
        this.authService.loadUserInfoDTO().subscribe();
      }
      const subscription = this.checkinUserParkingRegistration?.subscriptionAssignment.subscription;
      const fallbackParkingTariffs = subscription?.denyForeignParkingSubscriptionFallback || !this.checkinUserParkingRegistration?.zone?.foreignParkersAllowed ?
                                      null : this.checkinUserParkingRegistration?.zone?.foreignParkingSubscription?.tariffs;
      this.updateParkingTimes(subscription?.tariffs,
                  this.checkinUserParkingRegistration?.subscriptionAssignment?.validTo?.toUTC(), subscription?.maxParkingDurationInMin, fallbackParkingTariffs);
    });
  }

  public checkout(): void {
    const checkoutDTO = new CheckoutUserParkingDTO({sessionId: this.checkinUserParkingRegistration.sessionId});

    this.parkingService.checkout(checkoutDTO).subscribe(val => {
      this.selectedParkingProcessInfoIndex = 0;
      this.initForm(this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex)?.activeCheckinInfo);
    });
  }

  public resetZone(event: any): void {
    if (event.isInteracted) {
      this.parkingHelper.resetZone(this.parkingForm);
    }
  }

  public resetSubscription(event: any): void {
    if (event.isInteracted) {
      this.parkingHelper.resetSubscription(this.parkingForm);
    }
  }

  public onClientValueChange(event: any): void {
    if (event) {
      this.errors.clientNotFound = null;
    }
  }

  public onZoneValueChange(event: any): void {
    if (event) {
      this.errors.zoneNotFound = null;
    }
  }

  public onSubscriptionValueChange(event: any): void {
    if (event) {
      this.errors.subscriptionNotFound = null;
    }
  }

  public onControlPlateValueChange(event: any): void {
    if (event) {
      this.errors.controlPlateNotFound = null;
    }
  }

  public onPreviousParkingProcess() {
    if (this.parkingService.parkingProcessInfos?.length > 0) {
      this.selectedParkingProcessInfoIndex--;
      if (this.selectedParkingProcessInfoIndex < 0) {
        this.selectedParkingProcessInfoIndex = Math.max(this.parkingService.parkingProcessInfos.length - 1, 0);
      }
      this.initForm(this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex)?.activeCheckinInfo);
    }
  }

  public onNextParkingProcess() {
    if (this.parkingService.parkingProcessInfos?.length > 0) {
      this.selectedParkingProcessInfoIndex = (this.selectedParkingProcessInfoIndex + 1) % this.parkingService.parkingProcessInfos?.length;
      this.initForm(this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex)?.activeCheckinInfo);
    }
  }

  public onNewParkingProcess() {
    this.initForm(null);
  }

  private initForm(checkinUserParkingRegistration: CheckinUserParkingRegistrationDTO) {
    if (checkinUserParkingRegistration) {
      this.formSubscriptions?.unsubscribe();
      this.checkinUserParkingRegistration = checkinUserParkingRegistration;
      this.isCheckedIn = true;
      this.parkingHelper.handleParkingProcessInfo(this.user, this.parkingService.parkingProcessInfos?.at(this.selectedParkingProcessInfoIndex), this.parkingForm);
      this.setFormFieldsEnabledState(false);
      this.dataLoaded = true;
    } else {
      this.isParkingPossible = false;
      this.parkingForm.reset();
      this.checkinUserParkingRegistration = null;
      this.isCheckedIn = false;
      this.assignFormFieldSubscriptions();
      this.parkingHelper.createParkingProcessDropdownFieldsValues(this.user);
      if (this.clientDropdownValues.length === 1) {
        this.parkingForm.controls.clientId.setValue(this.clientDropdownValues[0].id);
      }
      this.parkingHelper.handleVehicleFields(this.user, this.parkingForm.controls.subscriptionAssignmentId.value, this.parkingForm.controls.zoneId.value);
      this.setFormFieldsEnabledState(true);
    }
  }

  private assignFormFieldSubscriptions() {
    this.formSubscriptions?.unsubscribe();
    this.formSubscriptions = new Subscription();
    this.parkingHelper.assignFormFieldSubscriptions(this.parkingForm, this.formSubscriptions, this.user);
    this.formSubscriptions.add(
      this.parkingForm.controls.subscriptionAssignmentId.valueChanges.subscribe((subscriptionId) => {
        if (!subscriptionId) {
          this.isParkingPossible = false;
          return;
        }
        const formValues = this.parkingForm.getRawValue();
        if (!formValues.zoneId || !formValues.subscriptionAssignmentId) {
          return;
        }
        const zone = this.parkingProcessZoneInfoDTO?.find(z => z.id === formValues.zoneId);
        const assignment = zone?.subscriptionAssignments.find(s => s.id === formValues.subscriptionAssignmentId);
        const subscription = assignment?.subscription;
        if (!subscription) {
          return;
        }
        this.parkingService.getParkingQuotaInfo(subscription.id).subscribe((quotaInfo) => {
          this.parkingQuotaInfo = quotaInfo;
          this.parkingQuotaInfoTextParams = null;
          if (this.parkingQuotaInfo?.remainingParkingQuotaInMinutes != null) {
            this.parkingQuotaInfoTextParams = {
              remainingQuotaInHours:  StringHelper.transformToHoursMinutesRepresentation(this.parkingQuotaInfo.remainingParkingQuotaInMinutes > 0 ? this.parkingQuotaInfo.remainingParkingQuotaInMinutes : 0),
              quotaInHours: this.parkingQuotaInfo.totalParkingQuotaInHours,
              duration: this.translate.instant('i18n.shared.parkingQuotaDuration.' + ParkingQuotaDuration[this.parkingQuotaInfo.parkingQuotaDuration]),
            }
          }

          if (this.parkingQuotaInfo?.remainingParkingQuotaInMinutes != null && this.parkingQuotaInfo?.remainingParkingQuotaInMinutes < 0) {
            this.parkingQuotaInfo.remainingParkingQuotaInMinutes = 0;
          }
          this.refreshParkingTimesFromForm();
        });
      })
    );
  }

  private refreshParkingTimesFromForm() {
    const formValues = this.parkingForm.getRawValue();
    if (!formValues.zoneId || !formValues.subscriptionAssignmentId) {
      return;
    }
    const zone = this.parkingProcessZoneInfoDTO?.find(z => z.id === formValues.zoneId);
    const assignment = zone?.subscriptionAssignments.find(s => s.id === formValues.subscriptionAssignmentId);
    const subscription = assignment?.subscription;
    const tariffs = subscription?.tariffs;
    const fallbackParkingTariffs = subscription.denyForeignParkingSubscriptionFallback || !zone?.foreignParkersAllowed ?
                          null : zone?.foreignParkingSubscriptionInfos?.find(f => f.subscription.id === zone.defaultForeignParkingSubscriptionId)?.subscription?.tariffs;
    const maxParkingDurationInMinutes = MaxParkingDurationHelper.resolveEffectiveMaxParkingDurationInMinutes(
      subscription?.maxParkingDurationInMin,
      this.parkingQuotaInfo?.remainingParkingQuotaInMinutes
    );

    if (subscription?.activeSessionsCount > 0 && subscription?.multipleCarsPerUserTreatAs === MultipleCarsPerUserTreatAs.Forbidden ||
        subscription?.activeSessionsCount > 1 && subscription?.multipleCarsPerUserTreatAs !== MultipleCarsPerUserTreatAs.MultipleCars) {
      this.isParkingPossible = false;
      return;
    }
    this.updateParkingTimes(tariffs, assignment?.validTo?.toUTC(), maxParkingDurationInMinutes, fallbackParkingTariffs);
  }

  /* private updateParkingEndTimeText(value: DurationMembers) {
    const parkingDurationInMinutes = MultiFieldDurationPickerHelper.calculateTotalMinutes(value.days, value.hours, value.minutes, false);
    this.parkingEndTimeText = '';
    if (parkingDurationInMinutes > 0) {
      const now = DateTime.now();
      const endTime = now.plus({minutes: parkingDurationInMinutes});
      this.parkingEndTimeText = endTime.toLocaleString(DateTime.DATETIME_SHORT);
    }
  } */

  private setFormFieldsEnabledState(enable: boolean): void {
    if (enable) {
      if (this.clientDropdownValues.length > 1 || this.clientDropdownValues.length === 0 || this.errors.clientNotFound) {
        this.parkingForm.controls.clientId.enable({emitEvent: false});
      }
      if (this.subscriptionsDropdownValues.length > 1 || this.errors.subscriptionNotFound) {
        this.parkingForm.controls.subscriptionAssignmentId.enable({emitEvent: false});
      }
      if (this.zoneDropdownValues.length > 1 || this.errors.zoneNotFound) {
        this.parkingForm.controls.zoneId.enable({emitEvent: false});
      }
      this.parkingForm.controls.parkingSpaceNumber.enable({emitEvent: false});
      this.parkingForm.controls.controlPlateId.enable({emitEvent: false});
      this.parkingForm.controls.newVehicle.controls.countryCode.enable({emitEvent: false});
      this.parkingForm.controls.newVehicle.controls.numberPlate.enable({emitEvent: false});
    } else {
      this.parkingForm.controls.clientId.disable({emitEvent: false});
      this.parkingForm.controls.subscriptionAssignmentId.disable({emitEvent: false});
      this.parkingForm.controls.zoneId.disable({emitEvent: false});
      this.parkingForm.controls.parkingSpaceNumber.disable({emitEvent: false});
      this.parkingForm.controls.controlPlateId.disable({emitEvent: false});
      this.parkingForm.controls.newVehicle.controls.type.disable({emitEvent: false});
      this.parkingForm.controls.newVehicle.controls.countryCode.disable({emitEvent: false});
      this.parkingForm.controls.newVehicle.controls.numberPlate.disable({emitEvent: false});
    }
  }

  private updateParkingTimes(tariffs: ParkingProcessTariffInfoDTO[], tariffsMaxValidityTime: DateTime,
                              subscriptionsMaxParkingDuration: number, fallbackParkingTariffs: ParkingProcessTariffInfoDTO[], ): void {
    const startTimeUtc = this.checkinUserParkingRegistration?.registrationDateTime?.toUTC() ?? DateTime.utc();
    const parkingTimesState = ParkingTimesHelper.calculateParkingTimesState(
      startTimeUtc,
      tariffs,
      tariffsMaxValidityTime,
      subscriptionsMaxParkingDuration,
      fallbackParkingTariffs,
      {
        maxEndTime: this.maxEndTime,
        hasTariffs: this.hasTariffs,
      }
    );

    this.isParkingPossible = parkingTimesState.isParkingPossible;
    this.maxParkingDuration = parkingTimesState.maxParkingDuration;
    this.isParkingUnlimited = parkingTimesState.isParkingUnlimited;
    this.maxEndTime = parkingTimesState.maxEndTime;
    this.hasTariffs = parkingTimesState.hasTariffs;
    //this.parkingForm.controls.parkingDuration.controls.maxParkingDuration.setValue(this.maxParkingDuration);
  }
}
